import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../components/layout/Header';
import {
  cancelCampaign, getCampaign, getCampaignRecipients, getLineProtection, pauseCampaign, resumeCampaign,
} from '../services/apiCampaigns';
import { EstadoChip, EstadoDeLinea, pct } from '../components/campaigns/shared';

const FILTROS = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'pendiente', etiqueta: 'En espera' },
  { valor: 'enviado', etiqueta: 'Enviados' },
  { valor: 'respondio', etiqueta: 'Respondieron' },
  { valor: 'omitido', etiqueta: 'Omitidos' },
  { valor: 'fallido', etiqueta: 'Fallidos' },
  { valor: 'baja', etiqueta: 'Bajas' },
];

const ESTADO_DESTINATARIO = {
  pendiente: { label: 'En espera', clase: 'text-slate-500' },
  en_cola:   { label: 'Enviando…', clase: 'text-primary' },
  enviado:   { label: 'Enviado', clase: 'text-emerald-600' },
  fallido:   { label: 'Falló', clase: 'text-red-600' },
  omitido:   { label: 'Omitido', clase: 'text-amber-600' },
};

const ENTREGA = { sent: 'Enviado', delivered: 'Entregado', read: 'Leído', failed: 'Falló', pending: 'Pendiente' };

const Kpi = ({ valor, etiqueta, detalle }) => (
  <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
    <p className="text-2xl font-black text-slate-900 dark:text-white tabular-nums">{valor}</p>
    <p className="text-[11px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider mt-1">{etiqueta}</p>
    {detalle && <p className="text-[11px] text-slate-400 mt-0.5">{detalle}</p>}
  </div>
);

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [abierto, setAbierto] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const { data: campana } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => (await getCampaign(id)).data,
    refetchInterval: 20000,
  });

  const { data: destinatarios } = useQuery({
    queryKey: ['campaign-recipients', id, filtro, pagina],
    queryFn: async () => (await getCampaignRecipients(id, { estado: filtro || undefined, page: pagina })).data,
    refetchInterval: 20000,
    keepPreviousData: true,
  });

  const { data: proteccion } = useQuery({
    queryKey: ['campaigns-protection'],
    queryFn: async () => (await getLineProtection()).data,
    refetchInterval: 30000,
  });

  const accion = async (fn, confirmacion) => {
    if (confirmacion && !window.confirm(confirmacion)) return;
    setOcupado(true);
    try {
      await fn(id);
      queryClient.invalidateQueries(['campaign', id]);
      queryClient.invalidateQueries(['campaign-recipients', id]);
      queryClient.invalidateQueries(['campaigns']);
    } finally {
      setOcupado(false);
    }
  };

  if (!campana) return <div className="p-8 text-text-secondary">Cargando…</div>;
  if (campana.estado === 'borrador') {
    navigate(`/campaigns/${id}/edit`, { replace: true });
    return null;
  }

  const e = campana.estadisticas ?? {};
  const procesados = (e.enviados ?? 0) + (e.fallidos ?? 0) + (e.omitidos ?? 0);
  const linea = proteccion?.lineas?.find(l => l.linea_id === campana.whatsapp_account_id);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark">
      <Header title="Campaña" />

      <div className="flex-1 overflow-y-auto p-4 md:p-8 no-scrollbar">
        <div className="max-w-6xl mx-auto space-y-6">
          <Link to="/campaigns" className="text-sm font-bold text-slate-500 hover:text-primary inline-flex items-center gap-1">
            <span className="material-symbols-outlined text-[18px]">arrow_back</span> Campañas
          </Link>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{campana.nombre}</h2>
                <EstadoChip estado={campana.estado} />
                {campana.recurrente && <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-primary/10 text-primary">Automática</span>}
              </div>
              <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
                {campana.modo === 'ia' ? 'Redactada por IA' : 'Plantilla'} · Línea {campana.linea ?? '—'} · Lanzada {fecha(campana.lanzada_at)}
                {campana.creador ? ` por ${campana.creador}` : ''}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {['enviando', 'programada'].includes(campana.estado) && (
                <button disabled={ocupado} onClick={() => accion(pauseCampaign)}
                  className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1.5 disabled:opacity-50">
                  <span className="material-symbols-outlined text-[18px]">pause</span> Pausar
                </button>
              )}
              {campana.estado === 'pausada' && (
                <button disabled={ocupado} onClick={() => accion(resumeCampaign)}
                  className="px-4 py-2 rounded-xl text-sm font-bold bg-primary hover:bg-primary/90 text-white flex items-center gap-1.5 disabled:opacity-50">
                  <span className="material-symbols-outlined text-[18px]">play_arrow</span> Reanudar
                </button>
              )}
              {['enviando', 'programada', 'pausada'].includes(campana.estado) && (
                <button disabled={ocupado} onClick={() => accion(cancelCampaign, 'Cancelar la campaña: los mensajes que faltan no se enviarán. ¿Seguro?')}
                  className="px-4 py-2 rounded-xl text-sm font-bold border border-slate-200 dark:border-white/10 text-slate-600 dark:text-gray-300 hover:bg-white dark:hover:bg-white/5 disabled:opacity-50">
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {campana.estado === 'pausada' && campana.pausa_motivo && (
            <div className="flex gap-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl p-4">
              <span className="material-symbols-outlined text-amber-600">warning</span>
              <div>
                <p className="text-sm font-bold text-amber-800 dark:text-amber-200">Pausada</p>
                <p className="text-sm text-amber-700 dark:text-amber-300">{campana.pausa_motivo}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi valor={`${e.enviados ?? 0}`} etiqueta="Enviados" detalle={`de ${e.total ?? 0} · ${e.pendientes ?? 0} en espera`} />
            <Kpi valor={`${pct(e.entregados ?? 0, e.enviados ?? 0)}%`} etiqueta="Entregados" detalle={`${pct(e.leidos ?? 0, e.enviados ?? 0)}% leídos`} />
            <Kpi valor={e.respondieron ?? 0} etiqueta="Respondieron" detalle={`${pct(e.respondieron ?? 0, e.enviados ?? 0)}% de los enviados`} />
            <Kpi valor={e.ventas ?? 0} etiqueta="Ventas" detalle={e.monto_ventas ? `$${Number(e.monto_ventas).toLocaleString('es')}` : 'en 30 días'} />
            <Kpi valor={e.bajas ?? 0} etiqueta="Bajas" detalle={`${e.omitidos ?? 0} omitidos · ${e.fallidos ?? 0} fallidos`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
            <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="flex gap-1 p-2 border-b border-slate-100 dark:border-white/5 overflow-x-auto no-scrollbar">
                {FILTROS.map(f => (
                  <button key={f.valor} onClick={() => { setFiltro(f.valor); setPagina(1); }}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap ${filtro === f.valor ? 'bg-primary/10 text-primary' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5'}`}>
                    {f.etiqueta}
                  </button>
                ))}
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-white/5">
                {(destinatarios?.data ?? []).map(d => {
                  const est = ESTADO_DESTINATARIO[d.estado] ?? ESTADO_DESTINATARIO.pendiente;
                  return (
                    <li key={d.id}>
                      <button onClick={() => setAbierto(abierto === d.id ? null : d.id)} className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/[0.03]">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{d.nombre}</p>
                            <p className="text-[11px] text-slate-500 dark:text-gray-400 truncate">
                              {d.motivo && d.estado !== 'enviado' ? d.motivo : d.enviado_at ? `Enviado ${fecha(d.enviado_at)}` : d.telefono}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className={`text-xs font-bold ${est.clase}`}>{est.label}</p>
                            <p className="text-[10px] text-slate-400">
                              {d.baja_at ? 'Pidió la baja' : d.respondio_at ? 'Respondió' : d.entrega ? ENTREGA[d.entrega] : ''}
                            </p>
                          </div>
                        </div>
                      </button>
                      {abierto === d.id && (
                        <div className="px-4 pb-4 space-y-2">
                          {d.mensaje
                            ? <div className="bg-[#e7ffdb] dark:bg-emerald-900/30 text-sm text-slate-800 dark:text-gray-100 rounded-2xl rounded-tr-md px-4 py-3 whitespace-pre-wrap">{d.mensaje}</div>
                            : <p className="text-xs text-slate-400">El mensaje se redacta justo antes de enviarlo.</p>}
                          <Link to={`/contacts/${d.contact_id}/360`} className="text-xs font-bold text-primary hover:underline">Ver contacto</Link>
                        </div>
                      )}
                    </li>
                  );
                })}
                {destinatarios?.data?.length === 0 && <li className="px-4 py-10 text-center text-sm text-slate-400">Nadie en esta vista.</li>}
              </ul>
              {destinatarios?.last_page > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-white/5 text-xs">
                  <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className="font-bold text-slate-600 disabled:opacity-30">Anterior</button>
                  <span className="text-slate-400">Página {destinatarios.current_page} de {destinatarios.last_page}</span>
                  <button disabled={pagina >= destinatarios.last_page} onClick={() => setPagina(p => p + 1)} className="font-bold text-slate-600 disabled:opacity-30">Siguiente</button>
                </div>
              )}
            </div>

            <aside className="space-y-4">
              {linea && <EstadoDeLinea linea={linea} />}
              <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-5 space-y-2">
                <p className="text-[11px] font-black text-slate-500 dark:text-gray-400 uppercase tracking-wider">
                  {campana.modo === 'ia' ? 'Lo que se pidió comunicar' : 'Plantilla'}
                </p>
                <p className="text-sm text-slate-700 dark:text-gray-200 whitespace-pre-wrap">{campana.modo === 'ia' ? campana.intencion : campana.plantilla}</p>
                <p className="text-[11px] text-slate-400">{procesados} de {e.total ?? 0} procesados.</p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
